
import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { OrderItem, Product, ProductStatus } from '../types';
import { computeCartTotal } from '../utils/cartTotals';

import { devError } from '@/utils/devLog';
interface CartContextType {
  cartItems: OrderItem[];
  addToCart: (product: Product, quantity: number, configuration?: Record<string, string | string[]>) => void;
  removeFromCart: (produit_id: number, configuration?: Record<string, string | string[]>) => void;
  updateItemQuantity: (produit_id: number, quantity: number, configuration?: Record<string, string | string[]>) => void;
  clearCart: () => void;
  totalAmount: number;
}

const CartContext = createContext<CartContextType | null>(null);

const getInitialCart = (): OrderItem[] => {
    try {
        const item = window.localStorage.getItem('cartItems');
        return item ? JSON.parse(item) : [];
    } catch (error) {
        devError("Failed to parse cart items from localStorage", error);
        return [];
    }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cartItems, setCartItems] = useState<OrderItem[]>(getInitialCart);

    useEffect(() => {
        try {
            window.localStorage.setItem('cartItems', JSON.stringify(cartItems));
        } catch (error) {
            devError("Failed to save cart items to localStorage", error);
        }
    }, [cartItems]);
    
    const addToCart = (product: Product, quantity: number, configuration?: Record<string, string | string[]>) => {
        // --- GUARD: Prevent adding unavailable products ---
        if (product.statut === ProductStatus.SOLD_OUT) {
            alert("Ce produit est épuisé et ne peut pas être ajouté au panier.");
            return;
        }
        if (product.statut === ProductStatus.INCOMING) {
            alert("Ce produit est en arrivage et n'est pas encore disponible.");
            return;
        }

        setCartItems(prevItems => {
            const configString = JSON.stringify(configuration || {});
            const existingItem = prevItems.find(item => 
                item.produit_id === product.id && 
                JSON.stringify(item.configuration || {}) === configString
            );

            if (existingItem) {
                return prevItems.map(item =>
                    item.produit_id === product.id && JSON.stringify(item.configuration || {}) === configString
                        ? { ...item, quantite: item.quantite + quantity }
                        : item
                );
            } else {
                return [...prevItems, {
                    produit_id: product.id,
                    nom_produit: product.nom,
                    prix_unitaire: product.prix, // The price might already be configured
                    quantite: quantity,
                    configuration,
                    delivery_method: product.delivery_method // Persist the delivery method
                }];
            }
        });
    };
    
    const removeFromCart = (produit_id: number, configuration?: Record<string, string | string[]>) => {
        const configString = JSON.stringify(configuration || {});
        setCartItems(prevItems => prevItems.filter(item => 
            !(item.produit_id === produit_id && JSON.stringify(item.configuration || {}) === configString)
        ));
    };

    const updateItemQuantity = (produit_id: number, quantity: number, configuration?: Record<string, string | string[]>) => {
        const configString = JSON.stringify(configuration || {});
        setCartItems(prevItems => prevItems.map(item =>
            item.produit_id === produit_id && JSON.stringify(item.configuration || {}) === configString
                ? { ...item, quantite: quantity }
                : item
        ).filter(item => item.quantite > 0)); // Remove item if quantity is 0 or less
    };

    const clearCart = () => {
        setCartItems([]);
    };
    
    const totalAmount = useMemo(() => computeCartTotal(cartItems), [cartItems]);

    const value = {
        cartItems,
        addToCart,
        removeFromCart,
        updateItemQuantity,
        clearCart,
        totalAmount
    };
    
    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
